<template>
  <v-card
    class="pa-5"
    v-if="hasPermission('task.list')"
    :style="{ border: '1px solid var(--primary-boarder-color)' }"
  >
    <ConfirmModal
      :text="'Do you want to drop this Task?'"
      :title="'Drop Task'"
      :color="'red'"
      :confirmOpen="confirmOpen"
      @cancelConfirm="cancelConfirm()"
      @confirm="confirm()"
    ></ConfirmModal>

    <v-row class="mb-2 pa-5 pb-1 d-flex justify-space-between align-center">
      <span class="text-h6 font-weight-bold">Task List</span>
      <v-btn
        v-if="hasPermission('project.task_create')"
        @click="openTaskDialog"
        color="#00B3FF"
        depressed
        class="white--text"
        >Add Task</v-btn
      >
    </v-row>
    <v-row class="mb-2 px-5">
      <v-col
        cols="12"
        xs="6"
        sm="4"
        md="2"
        lg="2"
        xl="2"
        class="ma-0 pa-0 pt-2"
      >
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Search"
          class="grey-lighten-3"
          background-color="#FAFAFA"
          outlined
          fieldset="#ddd"
          single-line
          hide-details
          dense
        ></v-text-field>
      </v-col>
      <v-col cols="12" xs="6" sm="4" md="2" lg="2" xl="2" class="ma-0 pa-0">
        <v-autocomplete
          v-model="selectedStage"
          :items="stages"
          item-text="name"
          item-value="name"
          label="Filter by Stage"
          clearable
          class="grey-lighten-3 ma-2"
          background-color="#FAFAFA"
          outlined
          fieldset="#ddd"
          dense
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="6" sm="4" md="2" lg="2" xl="2" class="ma-0 pa-0">
        <v-autocomplete
          v-model="selectedCategory"
          :items="categories"
          item-text="name"
          item-value="name"
          label="Filter by Category"
          clearable
          class="grey-lighten-3 ma-2"
          background-color="#FAFAFA"
          outlined
          fieldset="#ddd"
          dense
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="6" sm="4" md="2" lg="2" xl="2" class="ma-0 pa-0">
        <v-autocomplete
          v-model="selectedMember"
          :items="project.members"
          :item-text="formatMemberText"
          item-value="uuid"
          label="Filter by Member"
          clearable
          class="grey-lighten-3 ma-2"
          background-color="#FAFAFA"
          outlined
          fieldset="#ddd"
          dense
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="6" sm="4" md="2" lg="2" xl="2" class="ma-0 pa-0">
        <v-autocomplete
          v-model="filterStatus"
          :items="statusOptions"
          item-text="label"
          item-value="label"
          label="Filter by Status"
          clearable
          class="grey-lighten-3 ma-2"
          background-color="#FAFAFA"
          outlined
          fieldset="#ddd"
          dense
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" xs="6" sm="4" md="2" lg="2" xl="2" class="ma-0 pa-0">
        <v-btn
          depressed
          @click="clearFilters"
          dark
          color="#757575"
          :loading="loading"
          class="ma-2 py-4"
          outlined
          >clear all</v-btn
        >
      </v-col>
    </v-row>
    <v-divider class="border-opacity-25"></v-divider>
    <v-data-table
      :headers="headers"
      :items="sortedTasks"
      item-value="uuid"
      :hide-default-footer="true"
      :disable-sort="true"
      :disable-pagination="true"
      :hide-default-header="false"
      id="data-table"
    >
      <template v-slot:item="{ item, index }">
        <tr
          :class="[
            { 'first-row': index === 0 },
            index % 2 === 0 ? 'dark-row' : 'light-row',
            'custom-row',
          ]"
        >
          <td class="text-left">
            <router-link :to="'/task/info/' + item.uuid" class="router-link">
              <div class="text-h7 font-weight-medium text-black ">
                {{ item.task_code }}
              </div>
            </router-link>
          </td>
          <td class="text-left">
            <router-link
              :to="'/task/info/' + item.uuid"
              class="router-link  font-weight-medium"
            >
              <div router-link>
                {{ item.task_title }}
              </div>
            </router-link>
          </td>
          <td class="text-left">
            <avatar-group
              :avatars="
                item.members.map((member) => ({
                  profile_image: member.profile_image,
                  full_name: member.employer.full_name,
                }))
              "
            />
          </td>
          <td class="text-center">
            <v-chip
              small
              :color="
                item.stage && item.stage.color_code
                  ? item.stage.color_code
                  : 'primary'
              "
              class="white--text"
            >
              {{ item.stage ? item.stage.name : "" }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-chip
              small
              :color="
                item.category && item.category.color_code
                  ? item.category.color_code
                  : '#2979FF'
              "
              class="white--text"
            >
              {{ item.category ? item.category.name : "" }}
            </v-chip>
          </td>
          <td class="text-center">
            <v-chip
              small
              :color="getStatusColors(item.status).chip"
              :text-color="getStatusColors(item.status).text"
              dark
            >
              {{ item.status }}
            </v-chip>
            <v-menu v-model="statusMenuOpen[item.uuid]" offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="hasPermission('task.update_status')"
                  icon
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-item
                  v-for="status in statusOptions"
                  :key="status.id"
                  @click="selectStatus(item.uuid, status.label)"
                >
                  <v-list-item-content>{{ status.label }}</v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
          <td class="text-center">
            <div class="text-h7 font-weight-medium text-black ">
              {{ item.due_at }}
            </div>
          </td>
          <td class="text-left">
            <v-row class="d-flex justify-center">
              <v-btn icon @click="openEditTaskDialog(item)">
                <v-icon>mdi-pencil</v-icon>
              </v-btn>
              <v-icon
                @click="deleteTaskConfirm(item.uuid)"
                color="red"
                class="mx-1"
              >
                mdi-delete
              </v-icon>
            </v-row>
          </td>
        </tr>
      </template>
    </v-data-table>
    <v-row class="my-1">
        <Pagination
          v-model="page"
          :loading="loading"
          :total="total"
          :page="page"
        ></Pagination>
      </v-row>
    <v-dialog v-model="taskDialog" max-width="920px" persistent>
      <v-card class="scroll-disabled pa-5">
        <v-form
          ref="taskForm"
          @submit.prevent="isEditing ? updateTask() : createTask()"
        >
          <v-card-title>
            <v-row align="center" justify="space-between">
              <v-col cols="auto">
                <span class="text-h6 font-weight-bold">{{
                  isEditing ? "Edit Task" : "Add A New Task"
                }}</span>
              </v-col>
              <v-col cols="auto">
                <v-btn
                  v-if="!loading"
                  small
                  fab
                  depressed
                  class="mr-2 white--text"
                  color="blue"
                  sole
                  type="submit"
                >
                  <v-icon>mdi-check</v-icon>
                </v-btn>
                <v-btn
                  small
                  fab
                  depressed
                  class="mr-2 white--text"
                  color="gray"
                  sole
                  @click="close"
                >
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-title>
          <v-divider></v-divider>
          <v-col class="mt-2">
            <v-layout row v-if="errors">
              <v-flex>
                <Alert @dismissed="onDismissed" :text="errors"></Alert>
              </v-flex>
            </v-layout>
          </v-col>
          <v-card-text class="mt-5">
            <v-text-field
              outlined
              dense
              v-model="newTask.task_title"
              required
              label="Title*"
              :rules="[(v) => !!v || 'Title is required']"
              :error-messages="this.fieldErrors.task_title"
            ></v-text-field>
            <v-textarea
              outlined
              dense
              rows="4"
              v-model="newTask.description"
              label="Description*"
              :error-messages="this.fieldErrors.description"
            ></v-textarea>
            <v-row class="mt-0">
              <v-col cols="6" sm="12" md="6">
                <v-autocomplete
                  v-model="newTask.stage"
                  :items="this.stages"
                  item-text="name"
                  item-value="uuid"
                  label="Stage*"
                  dense
                  outlined
                  clearable
                  :error-messages="this.fieldErrors.stage"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" sm="12" md="6">
                <v-autocomplete
                  v-model="newTask.category"
                  :items="this.categories"
                  item-text="name"
                  item-value="uuid"
                  label="Category*"
                  dense
                  outlined
                  clearable
                  :error-messages="this.fieldErrors.category"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="6" sm="12" md="6">
                <DatePicker
                  v-model="newTask.start_at"
                  :selectValue="newTask.start_at"
                  @changeInput="newTask.start_at = $event"
                  :label="'Start Date*'"
                  :error="this.fieldErrors.start_at"
                  :maxDate="maxStartDate"
                ></DatePicker>
              </v-col>
              <v-col cols="6" sm="12" md="6">
                <DatePicker
                  v-model="newTask.due_at"
                  :selectValue="newTask.due_at"
                  @changeInput="newTask.due_at = $event"
                  :label="'Due Date*'"
                  :error="this.fieldErrors.due_at"
                  :minDate="minDueDate"
                ></DatePicker>
              </v-col>
            </v-row>
            <v-row class="mt-0">
              <v-col cols="6" sm="12" md="6">
                <v-autocomplete
                  v-model="memberSelection"
                  :items="project.members"
                  :item-text="formatMemberText"
                  item-value="uuid"
                  label="Select Members*"
                  dense
                  outlined
                  :multiple="multiUser"
                  clearable
                  :error-messages="this.fieldErrors.member_id"
                ></v-autocomplete>
              </v-col>
              <v-col cols="6" sm="12" md="6">
                <v-text-field
                  v-model="newTask.estimated_hours"
                  outlined
                  dense
                  :label="`Estimated Time Allocation* (${this.estUnit})`"
                  type="number"
                  :error-messages="this.fieldErrors.estimated_hours"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-card-text>
        </v-form>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import AvatarGroup from "@/components/shared/AvatarGroup";
const { mapGetters } = createNamespacedHelpers("auth");
import DatePicker from "@/components/shared/DatePicker";
import ConfirmModal from "@/components/shared/ConfirmModal";
import { TaskService } from "@/services/task-service.js";
import { ProjectService } from "@/services/project-service.js";
import Pagination from "@/components/shared/Pagination";
import Alert from "@/components/shared/Alert";
import { checkError } from "@/config/errors.js";
import { getStatusColors } from "@/utility/statusColors.js";

export default {
  components: {
    AvatarGroup,
    DatePicker,
    ConfirmModal,
    Pagination,
    Alert,
  },
  props: {
    project: Object,
    multiUser: Boolean,
    estUnit: String,
  },
  data() {
    return {
      defaultImage: require("@/assets/img/avatar.svg"),
      taskDialog: false,
      loading: false,
      statusMenuOpen: {},
      newTask: {
        task_title: "",
        description: "",
        member_id: [],
        start_at: null,
        due_at: null,
        estimated_hours: null,
        stage: "",
        category: "",
      },
      confirmOpen: false,
      dropId: null,
      selectedStatus: "",
      filterStatus: "",
      selectedStage: null,
      selectedCategory: null,
      selectedMember: null,
      search: "",
      page: 1,
      total: 0,
      totalTask: 0,
      categories: [],
      stages: [],
      statusOptions: [
        { id: 1, label: "IN-PROGRESS" },
        { id: 2, label: "COMPLETED" },
        { id: 3, label: "HOLD" },
        { id: 4, label: "PENDING" },
      ],
      isEditing: false,
      editTaskUuid: null,
      headers: [
        { text: "Task Code", value: "task_code" },
        { text: "Title", value: "task_title", align: "start" },
        { text: "Member/s", value: "memberSelection", align: "end" },
        { text: "Stage", value: "stage", align: "center" },
        { text: "Category", value: "category", align: "center" },
        { text: "Status", value: "status", align: "center" },
        { text: "Due Date", value: "due_at", align: "center" },
        { text: "Actions", value: "actions", sortable: false, align: "center" },
      ],
      tasks: [],
      errors: null,
      fieldErrors: [],
      minDueDate: "",
      maxStartDate: "",
    };
  },
  mounted() {
    this.getStagesAndCategories();
    this.getProjectTaskList();
    this.$parent.$on("task-updated-success", () => {
      this.loading = false;
      this.getProjectTaskList();
      this.close();
      this.onDismissed();
    });
    this.$parent.$on("task-created-success", () => {
      this.loading = false;
      this.getProjectTaskList();
      this.close();
      this.onDismissed();
    });
    this.$parent.$on("task-created-error", (errorData) => {
      this.loading = false;
      this.fieldErrors = errorData.errors;
      this.onTaskCreatedError(errorData);
    });
  },
  computed: {
    ...mapGetters({
      hasPermission: "hasPermission",
    }),
    sortedTasks() {
      if (!this.tasks) {
        return [];
      }
      const completedTasks = this.tasks.filter(
        (task) => task.status === "COMPLETED"
      );
      const nonCompletedTasks = this.tasks
        .filter((task) => task.status !== "COMPLETED")
        .reverse();
      return [...nonCompletedTasks, ...completedTasks];
    },
    memberSelection: {
      get() {
        return this.multiUser
          ? this.newTask.member_id
          : this.newTask.member_id[0] || null;
      },
      set(val) {
        if (this.multiUser) {
          this.newTask.member_id = val;
        } else {
          this.newTask.member_id = val ? [val] : [];
        }
      },
    },
  },
  watch: {
    multiUser(val) {
      if (val) {
        this.memberSelection = this.newTask.member_id;
      } else {
        this.memberSelection =
          this.newTask.member_id.length > 0 ? this.newTask.member_id[0] : null;
      }
    },
    page() {
      this.getProjectTaskList();
    },
    filterStatus() {
      this.getProjectTaskList();
    },
    selectedStage() {
      this.getProjectTaskList();
    },
    selectedCategory() {
      this.getProjectTaskList();
    },
    selectedMember() {
      this.getProjectTaskList();
    },
    search() {
      this.getProjectTaskList();
    },
    "newTask.start_at"(newVal) {
      this.minDueDate = newVal;
    },
    "newTask.due_at"(newVal) {
      this.maxStartDate = newVal;
    },
  },
  methods: {
    getStatusColors(status) {
      return getStatusColors(status);
    },
    formatMemberText(member) {
      if (member.employer && member.employer.speciality) {
        return `${member.employer.full_name} - ${member.employer.speciality.name}`;
      } else {
        return member.name;
      }
    },
    getStagesAndCategories() {
      this.loading = true;
      TaskService.getTaskStagesAndCategories()
        .then((response) => {
          this.loading = false;
          if (response.data) {
            this.categories = response.data.categories || [];
            this.stages = response.data.stages || [];
          }
        })
        .catch((error) => {
          this.loading = false;
          let data = error.response.data;
          this.errors = data;
        });
    },
    getProjectTaskList() {
      this.loading = true;
      ProjectService.getProjectTaskList(
        this.$route.params.uuid,
        this.search ?? "",
        this.filterStatus,
        this.selectedStage,
        this.selectedCategory,
        this.selectedMember,
        this.page
      )
        .then((response) => {
          this.loading = false;
          if (response.data.data) {
            this.tasks = response.data.data || [];
            this.total = response.data.meta.last_page || 0;
            this.totalTask = response.data.meta.total || 0;
          }
        })
        .catch((error) => {
          this.loading = false;
          this.tasks = [];
          let data = error.response.data;
          let errorOutput = checkError.errorFormatting(data);
          this.errors = errorOutput;
        });
    },
    openTaskDialog() {
      this.isEditing = false;
      this.resetNewTask();
      this.taskDialog = true;
    },
    openEditTaskDialog(task) {
      this.newTask.task_title = task.task_title;
      this.newTask.description = task.description;
      this.newTask.member_id = task.members.map((member) => member.uuid);
      this.newTask.start_at = task.start_at;
      this.newTask.due_at = task.due_at;
      this.newTask.estimated_hours = task.estimated_hours;
      this.newTask.stage = task.stage.uuid;
      this.newTask.category = task.category.uuid;

      this.isEditing = true;
      this.editTaskUuid = task.uuid;
      this.taskDialog = true;
    },
    createTask() {
      const isValid = this.$refs.taskForm.validate();
      if (isValid) {
        this.loading = true;
        this.onDismissed();
        this.$emit("task-created", this.newTask);
      }
    },
    updateTask() {
      const isValid = this.$refs.taskForm.validate();
      if (isValid) {
        this.loading = true;
        this.onDismissed();
        this.$emit("task-update", {
          ...this.newTask,
          uuid: this.editTaskUuid,
        });
      }
    },
    deleteTaskConfirm(uuid) {
      this.dropId = uuid;
      this.confirmOpen = true;
    },
    selectStatus(uuid, status) {
      this.selectedStatus = status;
      this.statusMenuOpen[uuid] = false;
      this.$emit("update-task-status", { uuid, status });
    },
    deleteTask() {
      if (this.dropId) {
        this.$emit("delete-task", this.dropId);
      }
    },
    resetNewTask() {
      this.newTask = {
        task_title: "",
        description: "",
        member_id: [],
        start_at: null,
        due_at: null,
        estimated_hours: null,
        stage: "",
        category: "",
      };
    },
    close() {
      this.taskDialog = false;
      this.resetNewTask();
      this.onDismissed();
    },
    cancelConfirm() {
      this.confirmOpen = false;
    },
    confirm() {
      this.confirmOpen = false;
      this.deleteTask();
    },
    clearFilters() {
      this.selectedStage = null;
      this.selectedCategory = null;
      this.selectedMember = null;
      this.filterStatus = "";
      this.search = "";
      this.searchTasks();
    },
    searchTasks() {
      this.getProjectTaskList();
    },
    onDismissed() {
      this.errors = null;
      this.fieldErrors = [];
    },
    onTaskCreatedError(errorData) {
      let errorOutput = checkError.errorFormatting(errorData);
      this.errors = errorOutput;
    },
  },
};
</script>
